import type { ModelDefinition } from "@/models.js";

export const moonshotModels = [
	{
		id: "kimi-k2",
		name: "Kimi K2",
		description: "Moonshot's Kimi K2 model for general tasks.",
		family: "moonshot",
		releasedAt: new Date("2025-07-11"),
		providers: [
			{
				providerId: "groq",
				externalId: "moonshotai/kimi-k2-instruct",
				// Frequently over capacity on Groq (503)
				// Ref: https://groqstatus.com
				stability: "unstable",
				inputPrice: "1.0e-6",
				cachedInputPrice: "0.5e-6",
				outputPrice: "3.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 16384,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				// 404 model_not_found: moonshotai/kimi-k2-instruct no longer served
				deactivatedAt: new Date("2026-06-05"),
			},
			{
				providerId: "novita",
				externalId: "moonshotai/kimi-k2-instruct",
				// Streaming tool calls produce malformed JSON arguments
				stability: "unstable",
				inputPrice: "0.57e-6",
				outputPrice: "2.3e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 131072,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
			{
				providerId: "moonshot",
				externalId: "kimi-k2-0905-preview",
				inputPrice: "0.6e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.15e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 128000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				// 404 resource_not_found: kimi-k2-0905-preview no longer served
				deactivatedAt: new Date("2026-06-05"),
			},
			{
				providerId: "nebius",
				externalId: "moonshotai/Kimi-K2-Instruct",
				inputPrice: "0.5e-6",
				outputPrice: "2.4e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-04-25"),
			},
			{
				providerId: "bytedance",
				externalId: "kimi-k2-250905",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.12e-6",
				outputPrice: "2.5e-6",
				requestPrice: "0",
				contextSize: 256000,
				maxOutput: 32768,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
				// 404 InvalidEndpointOrModel.NotFound: kimi-k2-250905 no longer served
				deactivatedAt: new Date("2026-06-05"),
			},
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "Moonshot-Kimi-K2-Instruct",
				inputPrice: "0.574e-6",
				outputPrice: "2.294e-6",
				regions: [{ id: "cn-beijing" }],
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				webSearch: true,
				jsonOutput: false,
				deactivatedAt: new Date("2026-07-08"),
			},
		],
	},
	{
		id: "kimi-k2-thinking",
		name: "Kimi K2 Thinking",
		description: "Kimi K2 with extended thinking capabilities.",
		family: "moonshot",
		releasedAt: new Date("2025-11-06"),
		providers: [
			{
				providerId: "moonshot",
				externalId: "kimi-k2-thinking",
				inputPrice: "0.6e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.15e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "bytedance",
				externalId: "kimi-k2-thinking-251104",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.12e-6",
				outputPrice: "2.5e-6",
				requestPrice: "0",
				contextSize: 256000,
				maxOutput: 32768,
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "kimi-k2-thinking",
				inputPrice: "0.574e-6",
				outputPrice: "2.294e-6",
				regions: [{ id: "cn-beijing" }],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 16384,
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-07-08"),
			},
		],
	},
	{
		id: "kimi-k2-thinking-turbo",
		name: "Kimi K2 Thinking Turbo",
		description: "Faster turbo variant of Kimi K2 with extended thinking.",
		family: "moonshot",
		releasedAt: new Date("2025-11-06"),
		providers: [
			{
				providerId: "moonshot",
				externalId: "kimi-k2-thinking-turbo",
				// Frequently overloaded (429 engine_overloaded_error)
				stability: "unstable",
				inputPrice: "1.15e-6",
				outputPrice: "8.0e-6",
				cachedInputPrice: "0.15e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "kimi-k2.5",
		name: "Kimi K2.5",
		description:
			"Moonshot's native multimodal model with visual coding and agentic capabilities.",
		family: "moonshot",
		releasedAt: new Date("2026-01-26"),
		providers: [
			{
				providerId: "moonshot",
				externalId: "kimi-k2.5",
				inputPrice: "0.6e-6",
				outputPrice: "3.0e-6",
				cachedInputPrice: "0.1e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				reasoning: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
				],
			},
			{
				providerId: "together-ai",
				externalId: "moonshotai/Kimi-K2.5",
				// Together.ai intermittently returns 500 for this model (~98.7% uptime)
				// Ref: https://status.together.ai
				// Model page: https://www.together.ai/models/kimi-k2-5
				stability: "unstable",
				inputPrice: "0.5e-6",
				outputPrice: "2.8e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: false,
				jsonOutput: false,
				deprecatedAt: new Date("2026-05-21"),
			},
			{
				providerId: "nebius",
				// Streaming tool calls and response_format: json_object are unreliable on Nebius
				stability: "unstable",
				externalId: "moonshotai/Kimi-K2.5",
				deactivatedAt: new Date("2026-06-22"),
				inputPrice: "0.5e-6",
				cachedInputPrice: "0.02e-6",
				outputPrice: "2.5e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "kimi-k2.5",
				inputPrice: "0.574e-6",
				outputPrice: "3.011e-6",
				regions: [{ id: "cn-beijing" }],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 98304,
				reasoning: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: false,
			},
			{
				providerId: "embercloud",
				externalId: "kimi-k2.5",
				inputPrice: "0.405e-6",
				outputPrice: "1.98e-6",
				cachedInputPrice: "0.225e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				streaming: true,
				reasoning: false,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
				],
			},
			{
				providerId: "deepinfra",
				externalId: "moonshotai/Kimi-K2.5",
				inputPrice: "0.45e-6",
				cachedInputPrice: "0.07e-6",
				outputPrice: "2.25e-6",
				requestPrice: "0",
				contextSize: 256000,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "kimi-k2.6",
		name: "Kimi K2.6",
		description:
			"Moonshot's latest native multimodal model with long-term code writing, thinking, and agentic capabilities.",
		family: "moonshot",
		releasedAt: new Date("2026-04-20"),
		providers: [
			{
				providerId: "moonshot",
				externalId: "kimi-k2.6",
				inputPrice: "0.95e-6",
				cachedInputPrice: "0.16e-6",
				outputPrice: "4.0e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				reasoning: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
				],
			},
			{
				providerId: "canopywave",
				externalId: "moonshotai/kimi-k2.6",
				inputPrice: "0.5e-6",
				cachedInputPrice: "0.1e-6",
				outputPrice: "2.8e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "moonshotai/kimi-k2.6",
				inputPrice: "0.95e-6",
				cachedInputPrice: "0.16e-6",
				outputPrice: "4.0e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "together-ai",
				externalId: "moonshotai/Kimi-K2.6",
				test: "skip",
				inputPrice: "1.2e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "4.5e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: false,
				jsonOutput: false,
			},
		],
	},
	{
		id: "kimi-k2.7-code",
		name: "Kimi K2.7 Code",
		description:
			"Kimi's most intelligent coding model with a native multimodal architecture supporting text, image, and video input, thinking modes, and agent tasks.",
		family: "moonshot",
		releasedAt: new Date("2026-06-12"),
		providers: [
			{
				providerId: "moonshot",
				externalId: "kimi-k2.7-code",
				inputPrice: "0.95e-6",
				cachedInputPrice: "0.19e-6",
				outputPrice: "4.0e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				reasoning: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
				],
			},
		],
	},
] as const satisfies ModelDefinition[];
